import React from 'react'
import SignInContainer from './components/sign-in-container'
import SignInFormContainer from './components/sign-in-form-container'
import SignInSubmitButton from './components/sign-in-submit-button'
import SignInFooter from './components/sign-in-footer'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { FormInput } from './components/form-input'
import {
  socialiteCallBackSocialiteCallbackProviderPostMutation,
  trialVerifyEmailTrialVerifyEmailPostMutation,
} from '@/generated/@tanstack/react-query.gen'
import { toast } from 'react-toastify'
import { signInInputSchema } from './data/sign-in-schema'
import SignInTermPolicy from './components/sign-in-term-policy'
import SignInSocial, { ILoginSocialCallBack } from './components/sign-in-social'
import { parseProfileResponse } from '@/hooks/getAuth'

interface Props {
  onNoAccount: (email: string) => void
  onHasAccount: (email: string) => void
  onLoginSocialSuccess: (data: any) => void
}

const formSchema = z.object({
  email: signInInputSchema.shape.email,
})

type FormValues = z.infer<typeof formSchema>

export default function SignInInputEmail({
  onNoAccount,
  onHasAccount,
  onLoginSocialSuccess,
}: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  })

  const email = watch('email')

  const { mutate, isPending: isPendingVerifyEmail } = useMutation({
    ...trialVerifyEmailTrialVerifyEmailPostMutation({}),
    // TODO: Update mutation response type after backend schema update
    onSuccess: (response: any) => {
      toast.success(response.msg)
      if (response.status !== 'success') return

      if (response.email_existed) {
        onHasAccount(email)
      } else {
        onNoAccount(email)
      }
    },
    onError: () => toast.error('Something went wrong. Please try again.'),
  })

  const onSubmit = (form: FormValues) =>
    mutate({
      body: {
        email: form.email,
      },
    })

  const { mutate: loginSocialMutate, isPending: isPendingLoginSocial } =
    useMutation({
      ...socialiteCallBackSocialiteCallbackProviderPostMutation({}),
      onSuccess: (response: any) => {
        const parse = parseProfileResponse(response)
        return onLoginSocialSuccess(parse)
      },
      onError: () => toast.error('Something went wrong. Please try again.'),
    })

  const onLoginWithSocial = (callback: ILoginSocialCallBack) => {
    loginSocialMutate({
      body: {
        code: callback.code || null,
        token: callback.access_token || null,
      },
      path: {
        provider: callback.provider,
      },
    })
  }

  const isPending = isPendingVerifyEmail || isPendingLoginSocial

  return (
    <SignInContainer loading={isPending}>
      <SignInFormContainer title="Sign Up or Log In">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex space-y-4 flex-col py-4">
            <div className="flex mb-2 space-y-4 flex-col">
              <FormInput
                type="email"
                placeholder="example@domain.com"
                {...register('email')}
                error={errors.email}
              />
            </div>

            <SignInSubmitButton text="Continue with email" />
            <SignInSocial onLoginSocialCallback={onLoginWithSocial} />
            <SignInTermPolicy />
          </div>
        </form>
      </SignInFormContainer>
      <SignInFooter />
    </SignInContainer>
  )
}
